import React, { useState, useEffect } from 'react';
import { previousResults as mockPreviousResults } from '../mockData';
import { Calendar, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { updatePageSEO } from '../utils/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreviousResults = () => {
  const [previousResults, setPreviousResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Update SEO for Previous Results page
    updatePageSEO({
      path: '/previous-results',
      title: 'Morning Sunday Teer Previous Result History | FR & SR Archive',
      description: 'View complete Morning Sunday Teer Previous Result history from 2020 to today. Check past FR and SR results, analyze patterns and trends.',
      robots: 'index, follow'
    });
    
    fetchPreviousResults();
  }, []);

  const fetchPreviousResults = async () => {
    try {
      setLoading(true);
      // Fetch all historical results (up to 5000)
      const response = await axios.get(`${API}/results/previous?limit=5000`);
      setPreviousResults(response.data);
    } catch (err) {
      console.error('Error fetching previous results:', err);
      // Keep using mock data if API fails
      setPreviousResults(mockPreviousResults);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Calendar className="w-12 h-12 text-coral-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Morning Teer Previous Results
          </h1>
          <p className="text-lg text-gray-600">
            Complete history of Shillong Morning Teer results
          </p>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-coral-600 mb-4"></div>
              <p className="text-gray-600 text-lg">Loading previous results...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-coral-600 text-white">
                    <th className="px-6 py-4 text-left font-semibold uppercase tracking-wide">
                      Date
                    </th>
                    <th className="px-6 py-4 text-center font-semibold uppercase tracking-wide">
                      F/R (10:30 AM)
                    </th>
                    <th className="px-6 py-4 text-center font-semibold uppercase tracking-wide">
                      S/R (11:30 AM)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {previousResults.map((result, index) => (
                    <tr
                      key={index}
                      className="bg-white hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {result.date}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border-[3px] border-coral-600 text-gray-900 font-bold text-xl">
                          {result.fr}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border-[3px] border-coral-600 text-gray-900 font-bold text-xl">
                          {result.sr}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
          <div className="flex items-start">
            <TrendingUp className="w-6 h-6 text-blue-500 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Understanding Previous Results
              </h3>
              <p className="text-gray-700">
                Reviewing previous Morning Teer results helps you understand number patterns and trends over time. 
                While past results cannot predict future outcomes, many followers use this data to study frequency 
                and observe which numbers appear more often.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviousResults;
